import * as z from 'zod';

import { FulfillmentStrategy } from '@invenco/products-interface';
import {
  OrderLineManagedAction,
  OrderLineStatus,
  type OrderCurrency,
} from '@invenco/sales-interface/interfaces';

// Order-level context the order lines table needs to render its lines.
export type OrderLinesOrderContext = {
  currency?: OrderCurrency;
  channelId?: string | null;
};

// Input order line shape before table-only normalization.
export type OrderLine = {
  id: string;
  lineNumber?: number | null;
  skuId?: string | null;
  skuName: string;
  externalSkuName?: string | null;
  description: string;
  qtyOrdered: number;
  unitPrice: number;
  discountRate?: number | null;
  discountAmount?: number | null;
  status?: OrderLineStatus | null;
  qtyReserved?: number | null;
  qtyCommitted?: number | null;
  qtyShipped?: number | null;
  qtyBalance?: number | null;
  assemblyOrderLineId?: string | null;
  assemblyComponentQty?: number | null;
  managedActions?: OrderLineManagedAction[] | null;
  fulfillmentStrategy?: FulfillmentStrategy | null;
};

// Normalized order line shape used internally by the order lines table.
export type OrderLineRow = Omit<OrderLine, 'qtyOrdered' | 'unitPrice'> & {
  qtyOrdered?: number;
  unitPrice?: number;
  discount: string;
  lineTotal: number;
  components?: OrderLineRow[];
};

export type OrderLineAddInput = Pick<
  OrderLine,
  'skuName' | 'description' | 'qtyOrdered' | 'unitPrice' | 'fulfillmentStrategy'
> & {
  discountRate: number;
  discountAmount: number;
};

export type OrderLineUpdatePatch = Partial<
  Pick<
    OrderLineAddInput,
    'skuName' | 'description' | 'qtyOrdered' | 'unitPrice' | 'discountRate' | 'discountAmount'
  >
>;

export const orderLineEditableSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  qtyOrdered: z.number('Ordered quantity is required').int().nonnegative(),
  unitPrice: z.number('Unit cost is required').nonnegative(),
  discount: z.string().regex(/^[0-9]*(\.[0-9]{0,2})?%?$/, 'Use a number or percentage'),
});

export type OrderLineEditableValues = z.output<typeof orderLineEditableSchema>;

function roundMoney(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function getDiscountFields(
  discount: string | null | undefined,
): Pick<OrderLineAddInput, 'discountRate' | 'discountAmount'> {
  const formattedDiscount = discount?.trim() ?? '';
  if (formattedDiscount.endsWith('%')) {
    return { discountRate: Number(formattedDiscount.slice(0, -1)) || 0, discountAmount: 0 };
  }

  return { discountRate: 0, discountAmount: Number(formattedDiscount) || 0 };
}

export function getOrderLineDiscountInputValue({
  discountRate,
  discountAmount,
}: Pick<OrderLine, 'discountRate' | 'discountAmount'>) {
  if (discountRate && discountRate > 0) return `${discountRate}%`;
  return discountAmount?.toString() ?? '0%';
}

export function getOrderLineDiscountAmount(line: Partial<OrderLineRow>) {
  const discountFields = line.discount !== undefined ? getDiscountFields(line.discount) : line;
  const qtyOrdered = line.qtyOrdered ?? 0;
  const unitPrice = line.unitPrice ?? 0;

  if (qtyOrdered <= 0) return 0;
  if (discountFields.discountRate && discountFields.discountRate > 0) {
    return qtyOrdered * unitPrice * (discountFields.discountRate / 100);
  }

  return discountFields.discountAmount ?? 0;
}

export function getOrderLineTotal(line: Partial<OrderLineRow>) {
  const qtyOrdered = line.qtyOrdered ?? 0;
  if (qtyOrdered <= 0) return 0;

  return roundMoney(qtyOrdered * (line.unitPrice ?? 0) - getOrderLineDiscountAmount(line));
}

export function normalizeOrderLine(line: OrderLine): OrderLineRow {
  const discount = getOrderLineDiscountInputValue(line);
  return {
    ...line,
    discount,
    lineTotal: getOrderLineTotal({ ...line, discount }),
  };
}

export function isOrderLineManaged(line: Pick<OrderLineRow, 'managedActions'>) {
  return line.managedActions?.includes(OrderLineManagedAction.EDIT) ?? false;
}

export function isBundleComponentLine(line: Pick<OrderLineRow, 'assemblyOrderLineId'>) {
  return Boolean(line.assemblyOrderLineId);
}
